import PropTypes from 'prop-types';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';

import WorkExperienceForm from './WorkExperienceForm/WorkExperienceForm';
import WorkExperienceDetails from './WorkExperienceDetails/WorkExperienceDetails';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'react-bootstrap';
import SvgIcon from 'components/SvgIcon/SvgIcon';

import { useDispatch } from 'react-redux';
import { update as updateUser } from 'redux/modules/auth';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';

import styles from './WorkExperience.module.scss';
import hocStyles from '../../hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const { headerContainer, header, editBtn, editWrp, loader } = hocStyles;

const WorkExperience = ({ user, isOwn, userData, isEmpty, placeholder }) => {
  const [workExperience, setWorkExperience] = useState(userData);
  const [displayedWorkExperience, setDisplayedWorkExperience] =
    useState(userData);
  const dispatch = useDispatch();
  const { lastUpdated, updating, updated } = useAuth();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setWorkExperience(userData);
    setDisplayedWorkExperience(userData);
  }, [userData]);

  const addNewWorkExperience = useCallback(
    (newWorkExperience) => {
      const updatedWorkExperience = [...workExperience, newWorkExperience];
      setWorkExperience(updatedWorkExperience);

      const updatedDisplayedWorkExperience = [
        ...displayedWorkExperience,
        newWorkExperience,
      ];
      setDisplayedWorkExperience(updatedDisplayedWorkExperience);
    },
    [workExperience, displayedWorkExperience]
  );

  const deleteWorkExperience = useCallback(
    (index) => {
      const workExperienceToDelete = displayedWorkExperience[index];
      const updatedDisplayedWorkExperience = displayedWorkExperience.filter(
        (_, idx) => index !== idx
      );
      setDisplayedWorkExperience(updatedDisplayedWorkExperience);

      const originalIndex = workExperience.findIndex(
        (we) => we === workExperienceToDelete
      );
      const updatedWorkExperience = workExperience.filter(
        (_, idx) => originalIndex !== idx
      );
      setWorkExperience(updatedWorkExperience);
      handleEdit('workexperience', updatedWorkExperience);
    },
    [workExperience, displayedWorkExperience]
  );

  const updateWorkExperience = useCallback(
    (updatedWorkExperience, index) => {
      const editedWorkExperience = _.cloneDeep(workExperience);
      const displayedItem = displayedWorkExperience[index];
      const originalIndex = workExperience.findIndex(
        (we) => we === displayedItem
      );
      editedWorkExperience[originalIndex] = updatedWorkExperience;
      setWorkExperience(editedWorkExperience);

      const updatedDisplayedWorkExperience = _.cloneDeep(
        displayedWorkExperience
      );
      updatedDisplayedWorkExperience[index] = updatedWorkExperience;
      setDisplayedWorkExperience(updatedDisplayedWorkExperience);
    },
    [workExperience, displayedWorkExperience]
  );

  const handleEdit = useCallback(
    (editMode, workExperienceUpdates = null) => {
      const updates = workExperienceUpdates
        ? { workexperience: workExperienceUpdates }
        : { workexperience: workExperience };

      if (Object.keys(workExperience).length > 0) {
        dispatch(
          updateUser(
            user,
            { ...updates, portfolioUpdate: true },
            false,
            [
              'skills:accepted(null)',
              'sectors',
              'tools',
              'education',
              'language',
              'diversity',
              'workexperience',
            ],
            '',
            editMode
          )
        ).then((freshUser) => {
          const fromApi = freshUser?.workexperience;
          const nextWork = Array.isArray(fromApi)
            ? fromApi
            : workExperienceUpdates != null
            ? workExperienceUpdates
            : workExperience;
          setWorkExperience(nextWork);
          setDisplayedWorkExperience(nextWork);
          trackEvent('freelancer-updates-work-experience');
          setEditing(false);
        });
      } else {
        setEditing(false);
      }
    },
    [dispatch, workExperience, user]
  );

  const renderWorkExperienceDetails = useCallback(() => {
    return displayedWorkExperience.map((workExperienceItem, index) => {
      const lastEntry = displayedWorkExperience.length - index === 1;
      const key = workExperienceItem.id
        ? `weDetails${workExperienceItem.id}`
        : `weDetails${workExperienceItem.ended_at}`;
      return (
        <WorkExperienceDetails
          key={key}
          lastEntry={lastEntry}
          index={index}
          workExperience={workExperienceItem}
          isOwn={isOwn}
          user={user}
          deleteWorkExperience={() => deleteWorkExperience(index)}
        />
      );
    });
  }, [displayedWorkExperience]);

  const renderWorkExperienceForms = () => {
    const noWorkExperience = displayedWorkExperience.length === 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {displayedWorkExperience.map((workExperienceItem, index) => {
          const isLast = index === displayedWorkExperience.length - 1;
          return (
            <WorkExperienceForm
              key={index}
              index={index}
              updateWorkExperience={updateWorkExperience}
              currentWorkExperience={workExperienceItem}
              isLast={isLast}
              deleteWorkExperience={() => deleteWorkExperience(index)}
              addNewWorkExperience={addNewWorkExperience}
            />
          );
        })}
        {noWorkExperience && (
          <Button
            bsStyle={'primary'}
            style={{
              alignSelf: 'center',
              width: '40%',
              maxWidth: '100%',
              marginTop: '10',
            }}
            onClick={() => addNewWorkExperience({})}
          >
            <strong>+ Add Work Experience</strong>
          </Button>
        )}
      </div>
    );
  };

  const instanceIsUpdating = useMemo(
    () => lastUpdated === 'workexperience',
    [lastUpdated]
  );

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !editing && <SvgIcon size="25" icon="tick_circle" color="#0fe2af" />;

  return (
    <div className={styles.container}>
      <div className={headerContainer}>
        <h3 className={header} style={{ marginTop: '5px' }}>
          Work Experience
        </h3>
        {user && isOwn && (
          <div className={editWrp} style={{ left: '2em', paddingRight: 30 }}>
            {successTick}
            {instanceIsUpdating && updating ? (
              <Loader loading className={loader} />
            ) : (
              <button
                id="edit-work-experience"
                className={editBtn}
                onClick={() => setEditing(true)}
                type="button"
              >
                <SvgIcon size="16" icon="pencil" color="#666666" />
              </button>
            )}
          </div>
        )}
      </div>
      {isEmpty && !editing && placeholder}
      {renderWorkExperienceDetails()}
      <br />

      <Modal show={editing} onHide={() => handleEdit('workexperience')}>
        <ModalHeader closeButton>
          <h4 className="lato-bold">Editing Work Experience</h4>
        </ModalHeader>
        <ModalBody bsClass={styles.editModal}>
          {renderWorkExperienceForms()}
        </ModalBody>
        <ModalFooter>
          <Button
            disabled={updating}
            onClick={() => {
              setWorkExperience(userData);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={updating}
            onClick={() => handleEdit('workexperience')}
            bsStyle="primary"
            className="ml--"
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

WorkExperience.propTypes = {
  user: PropTypes.object,
  isOwn: PropTypes.bool,
  userData: PropTypes.array,
  isEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default WorkExperience;
